import { resolveBookingAssetThumbnailSrc } from './bookingListDisplay';
import { coalesceAssetListRecord } from './assetListNormalize';
import { parseAssetCoordinates } from './assetGeo';
import { extractImagePathsFromRecordImagesField } from './assetImageResolve';

const ASSET_NESTED_KEYS = ['asset', 'Asset'] as const;

const GEO_SCALAR_KEYS = [
  'latitude',
  'longitude',
  'lat',
  'lng',
  'Latitude',
  'Longitude',
] as const;

function isImageFieldKey(key: string): boolean {
  if (/logo/i.test(key)) {
    return false;
  }
  if (key === 'images' || key === 'Images') {
    return true;
  }
  return /image|img|thumbnail|thumb|photo|picture|cover|banner|avatar/i.test(key);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function imageFieldHasValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === 'string') {
    return Boolean(value.trim());
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return true;
}

function copyImageFields(from: Record<string, unknown>, to: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(from)) {
    if (!isImageFieldKey(key) || !imageFieldHasValue(value)) {
      continue;
    }
    to[key] = value;
  }
  for (const nestKey of ASSET_NESTED_KEYS) {
    const nested = from[nestKey];
    if (!isPlainObject(nested)) {
      continue;
    }
    const target =
      isPlainObject(to[nestKey]) ? ({ ...(to[nestKey] as Record<string, unknown>) } as Record<string, unknown>) : {};
    for (const [key, value] of Object.entries(nested)) {
      if (isImageFieldKey(key) && imageFieldHasValue(value)) {
        target[key] = value;
      }
    }
    to[nestKey] = target;
  }
}

/** Prefer list/search geo when GET /assets/:id omits coordinates (distance badge needs them). */
function copyMissingGeoFields(from: Record<string, unknown>, to: Record<string, unknown>): void {
  if (parseAssetCoordinates(to)) {
    return;
  }
  if (!parseAssetCoordinates(from)) {
    return;
  }
  for (const key of GEO_SCALAR_KEYS) {
    if (to[key] === undefined || to[key] === null || to[key] === '') {
      if (from[key] !== undefined && from[key] !== null && from[key] !== '') {
        to[key] = from[key];
      }
    }
  }
  if (isPlainObject(from.address)) {
    const fromAddr = from.address;
    const toAddr = isPlainObject(to.address) ? { ...to.address } : {};
    for (const key of ['latitude', 'longitude', 'lat', 'lng'] as const) {
      if (
        (toAddr[key] === undefined || toAddr[key] === null || toAddr[key] === '') &&
        fromAddr[key] !== undefined &&
        fromAddr[key] !== null &&
        fromAddr[key] !== ''
      ) {
        toAddr[key] = fromAddr[key];
      }
    }
    if (Object.keys(toAddr).length > 0) {
      to.address = toAddr;
    }
  }
}

/**
 * GET /assets/:id often returns partner rows with empty `images[]` and no scalar URL while
 * search/list rows carry the working `asset_image_url`. Keep list/search image fields when
 * the API payload would render no usable gallery. Also keep list geo for distance display.
 */
export function mergeAssetApiRecordWithListSnapshot(
  apiRecord: Record<string, unknown>,
  listSnapshot: Record<string, unknown>,
): Record<string, unknown> {
  const api = coalesceAssetListRecord(apiRecord);
  const snap = coalesceAssetListRecord(listSnapshot);
  const apiImagesField = extractImagePathsFromRecordImagesField(api);
  const apiHasExplicitGallery =
    apiImagesField !== null && apiImagesField.length > 0;
  const snapSrc = resolveBookingAssetThumbnailSrc(snap);

  const out: Record<string, unknown> = { ...api };

  // Trust GET `images[]` when populated (e.g. after owner update). Otherwise borrow list/search URLs.
  if (!apiHasExplicitGallery && snapSrc) {
    copyImageFields(snap, out);
    delete out.images;
    delete out.Images;
    if (typeof snap.asset_image_url === 'string' && snap.asset_image_url.trim()) {
      delete out.cover_image;
      delete out.coverImage;
    }
  }

  copyMissingGeoFields(snap, out);
  return coalesceAssetListRecord(out);
}
